import { useEffect, useState } from 'react';
import { fetchMe } from '../services/api';

// The signed-in user's role, for hiding controls they cannot use.
//
// This is presentation only. Every rule it reflects is enforced server-side by
// the API's adminGuard, so a stale or spoofed answer here grants nothing - the
// worst it can do is show a button that then returns 403.
//
// Cached at module level rather than per component: several pages ask on the
// same load, and a CONCLUSIVE answer cannot change without a new sign-in.
//
// Conclusive is the load-bearing word. An identity holding seats in more than
// one tenant resolves no tenant until an org is picked, and /api/me then
// answers authenticated-but-tenantless: no tenant means no seat, no seat means
// no role, and an OWNER reads back as an ordinary member. Caching that answer
// hid every admin control for the rest of the load, because picking an org
// sets the x-tenant-id header for other calls but never re-asks this one.
let settled = null; // a conclusive answer, kept for the session
let inflight = null; // an attempt in progress, shared by concurrent mounts
let lastAttempt = 0;

const RETRY_AFTER_MS = 5000;

function conclusive(data) {
  return Boolean(data?.ok && data.tenantId);
}

export function loadMe() {
  if (settled) return settled;
  if (inflight) return inflight;
  // Inconclusive answers are retried, but not on every mount of every page -
  // an endpoint that never resolves would otherwise become a request storm.
  if (Date.now() - lastAttempt < RETRY_AFTER_MS) return Promise.resolve(null);

  lastAttempt = Date.now();
  inflight = fetchMe().catch(() => null).then((data) => {
    inflight = null;
    if (conclusive(data)) settled = Promise.resolve(data);
    return data;
  });
  return inflight;
}

// Call on sign-out, so the next user does not inherit the previous one's role,
// and on org selection, so the role is re-read against the tenant just chosen.
export function resetMe() {
  settled = null;
  inflight = null;
  lastAttempt = 0;
}

export function useMe() {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    loadMe().then((data) => {
      if (!live) return;
      setMe(data?.ok ? data : null);
      setLoading(false);
    });
    return () => { live = false; };
  }, []);

  return {
    me,
    loading,
    // Unknown is treated as NOT admin, so a failed lookup hides admin controls
    // rather than showing ones that will 403. The safe default is the quiet one.
    isAdmin: Boolean(me?.isAdmin),
    isGuest: Boolean(me?.isGuest),
    role: me?.role || '',
  };
}
